import React, { useEffect, useState } from "react";
import MaterialTable from "material-table";
import { useSelector, useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch
} from "@mui/material";
import { api } from "common";
import CircularLoading from "../components/CircularLoading";
import AlertDialog from "../components/AlertDialog";
import { ThemeProvider } from "@mui/material/styles";
import theme from "styles/tableStyle";
import TableStyle from "../components/Table/Style";
import localization from "../components/Table/Localization";
import { useTranslation } from "react-i18next";
import { SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions";

const emptyRule = {
  name: "",
  label: "",
  type: "surge",
  mode: "percentage",
  value: 0,
  enabled: false,
  payoutType: "rider_only",
  riderSharePercent: 100,
  adminSharePercent: 0,
  scope: "global",
  carType: "",
  startTime: "22:00",
  endTime: "06:00",
  priority: 0
};

export default function FareRules() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();
  const settings = useSelector(state => state.settingsdata.settings);
  const farerulesdata = useSelector(state => state.farerulesdata);
  const cartypes = useSelector(state => state.cartypes.cars || []);
  const { editFareRule } = api;
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(emptyRule);
  const [alert, setAlert] = useState({ open: false, msg: "" });

  useEffect(() => {
    const rules = farerulesdata.rules || [];
    setRows([...rules].sort((a, b) => (parseFloat(a.priority || 0) - parseFloat(b.priority || 0))));
  }, [farerulesdata.rules]);

  const openForm = (rule) => {
    setData(rule ? { ...rule } : { ...emptyRule, createdAt: new Date().getTime() });
    setOpen(true);
  };

  const closeForm = () => {
    setOpen(false);
    setSaving(false);
    setData(emptyRule);
  };

  const handleChange = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!settings.AllowCriticalEditsAdmin) {
      setAlert({ open: true, msg: t("demo_mode") });
      return;
    }
    if (!data.name || parseFloat(data.value || 0) <= 0) {
      setAlert({ open: true, msg: "Please add a name and a value greater than zero." });
      return;
    }
    if (data.scope === "carType" && !data.carType) {
      setAlert({ open: true, msg: "Please select a car type for this scoped rule." });
      return;
    }
    if (data.payoutType === "split" && (parseFloat(data.riderSharePercent || 0) < 0 || parseFloat(data.riderSharePercent || 0) > 100)) {
      setAlert({ open: true, msg: "Rider split must be between 0 and 100." });
      return;
    }
    if (data.type === "time_window" && (!data.startTime || !data.endTime)) {
      setAlert({ open: true, msg: "Please add start and end time." });
      return;
    }

    setSaving(true);
    await dispatch(editFareRule({
      ...data,
      label: data.label || data.name,
      value: parseFloat(data.value || 0),
      priority: parseFloat(data.priority || 0),
      riderSharePercent: data.payoutType === "split" ? parseFloat(data.riderSharePercent || 0) : (data.payoutType === "rider_only" ? 100 : 0),
      adminSharePercent: data.payoutType === "split" ? 100 - parseFloat(data.riderSharePercent || 0) : (data.payoutType === "admin_only" ? 100 : 0)
    }, data.id ? "Update" : "Add"));
    closeForm();
  };

  const columns = [
    { title: "Name", field: "name" },
    { title: "Type", field: "type" },
    { title: "Mode", field: "mode" },
    { title: "Value", field: "value", type: "numeric" },
    { title: "Payout", field: "payoutType" },
    { title: "Scope", field: "scope" },
    { title: "Car Type", field: "carType" },
    {
      title: "Time",
      field: "startTime",
      render: rowData => rowData.type === "time_window" ? `${rowData.startTime} - ${rowData.endTime}` : "-"
    },
    {
      title: "Enabled",
      field: "enabled",
      render: rowData => (
        <Switch
          checked={rowData.enabled === true}
          disabled={!settings.AllowCriticalEditsAdmin}
          onChange={() => dispatch(editFareRule({ ...rowData, enabled: !rowData.enabled }, "Update"))}
        />
      )
    }
  ];

  return farerulesdata.loading ? <CircularLoading /> : (
    <ThemeProvider theme={theme}>
      <MaterialTable
        title="Fare Rules"
        columns={columns}
        data={rows}
        style={{
          direction: isRTL === "rtl" ? "rtl" : "ltr",
          borderRadius: "8px",
          boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
          padding: "20px"
        }}
        options={TableStyle()}
        localization={localization(t)}
        editable={{
          onRowDelete: oldData =>
            settings.AllowCriticalEditsAdmin
              ? new Promise(resolve => {
                  setTimeout(() => {
                    resolve();
                    dispatch(editFareRule(oldData, "Delete"));
                  }, 300);
                })
              : new Promise(resolve => {
                  resolve();
                  setAlert({ open: true, msg: t("demo_mode") });
                })
        }}
        actions={[
          {
            icon: "add",
            tooltip: "Add fare rule",
            isFreeAction: true,
            onClick: () => openForm()
          },
          rowData => ({
            icon: "edit",
            tooltip: "Edit fare rule",
            onClick: () => openForm(rowData)
          })
        ]}
      />

      <Dialog open={open} onClose={closeForm} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>{data.id ? "Edit Fare Rule" : "Add Fare Rule"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Name" value={data.name || ""} onChange={e => handleChange("name", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Customer Label" value={data.label || ""} onChange={e => handleChange("label", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={data.type || "surge"}
                  onChange={e => {
                    const nextType = e.target.value;
                    setData(prev => ({
                      ...prev,
                      type: nextType,
                      payoutType: nextType === "platform_fee" ? "admin_only" : (prev.payoutType || "rider_only"),
                      riderSharePercent: nextType === "platform_fee" ? 0 : (prev.riderSharePercent || 100),
                      adminSharePercent: nextType === "platform_fee" ? 100 : (prev.adminSharePercent || 0)
                    }));
                  }}
                >
                  <MenuItem value="surge">Surge</MenuItem>
                  <MenuItem value="weather">Bad Weather</MenuItem>
                  <MenuItem value="time_window">Time Window</MenuItem>
                  <MenuItem value="platform_fee">Platform Fee</MenuItem>
                  <MenuItem value="custom">Custom Fee</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Mode</InputLabel>
                <Select label="Mode" value={data.mode || "percentage"} onChange={e => handleChange("mode", e.target.value)}>
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="flat">Flat</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Value" type="number" value={data.value || 0} onChange={e => handleChange("value", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Payout</InputLabel>
                <Select label="Payout" value={data.payoutType || (data.type === "platform_fee" ? "admin_only" : "rider_only")} onChange={e => handleChange("payoutType", e.target.value)}>
                  <MenuItem value="rider_only">Rider Only</MenuItem>
                  <MenuItem value="admin_only">Admin Only</MenuItem>
                  <MenuItem value="split">Split</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {data.payoutType === "split" ? <>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Rider Share %"
                  type="number"
                  value={data.riderSharePercent || 0}
                  onChange={e => {
                    const riderShare = parseFloat(e.target.value || 0);
                    handleChange("riderSharePercent", riderShare);
                    handleChange("adminSharePercent", 100 - riderShare);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Admin Share %" type="number" value={data.adminSharePercent || 0} disabled />
              </Grid>
            </> : null}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Scope</InputLabel>
                <Select label="Scope" value={data.scope || "global"} onChange={e => handleChange("scope", e.target.value)}>
                  <MenuItem value="global">Global</MenuItem>
                  <MenuItem value="carType">Car Type</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={data.scope !== "carType"}>
                <InputLabel>Car Type</InputLabel>
                <Select label="Car Type" value={data.carType || ""} onChange={e => handleChange("carType", e.target.value)}>
                  {cartypes.map(car => <MenuItem key={car.name} value={car.name}>{car.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Priority" type="number" value={data.priority || 0} onChange={e => handleChange("priority", e.target.value)} />
            </Grid>
            {data.type === "time_window" ? <>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Start Time" type="time" value={data.startTime || "22:00"} onChange={e => handleChange("startTime", e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="End Time" type="time" value={data.endTime || "06:00"} onChange={e => handleChange("endTime", e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
            </> : null}
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={data.enabled === true} onChange={e => handleChange("enabled", e.target.checked)} />} label="Enabled" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
        </DialogActions>
      </Dialog>
      <AlertDialog open={alert.open} onClose={() => setAlert({ open: false, msg: "" })}>{alert.msg}</AlertDialog>
    </ThemeProvider>
  );
}
